import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { useId } from 'react-id-generator';
import { useControlledDropdown } from '../../hooks/use_controlled_dropdown.js';
import { noop } from '../../utils/util.js';
import { Popper } from '../Popper/Popper.js';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["children", "returnFocusRef", "inline", "onHide", "onShow", "position", "showing"];
var FOCUS_ELEMENTS = {
  BUTTON: "BUTTON",
  MENU: "MENU",
  LAST_ITEM: "LAST_ITEM"
};
var ButtonMenuContext = /*#__PURE__*/React.createContext(null);
function useButtonMenuContext() {
  var context = React.useContext(ButtonMenuContext);

  if (context === null) {
    throw new TypeError("No ButtonMenuContext provided");
  }

  return context;
}

var ButtonMenuContainer = function ButtonMenuContainer(props) {
  var children = props.children,
      returnFocusRef = props.returnFocusRef,
      _props$inline = props.inline,
      inline = _props$inline === void 0 ? true : _props$inline,
      _props$onHide = props.onHide,
      _onHide = _props$onHide === void 0 ? noop : _props$onHide,
      _props$onShow = props.onShow,
      _onShow = _props$onShow === void 0 ? noop : _props$onShow,
      _props$position = props.position,
      position = _props$position === void 0 ? "left" : _props$position,
      showing = props.showing,
      rest = _objectWithoutProperties(props, _excluded);

  var _useId = useId(1, "dropdown-"),
      _useId2 = _slicedToArray(_useId, 1),
      id = _useId2[0];

  var _React$useState = React.useState(true),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      closeOnOutsideClick = _React$useState2[0],
      setCloseOnOutsideClick = _React$useState2[1];

  var _useControlledDropdow = useControlledDropdown({
    showing: showing
  }),
      showingState = _useControlledDropdow.showingState,
      setShowingState = _useControlledDropdow.setShowingState;

  var buttonRef = React.useRef(null);
  var menuRef = React.useRef(null);
  var lastItemRef = React.useRef();
  var focusItemRef = React.useRef(FOCUS_ELEMENTS.MENU);
  var setFocusItem = React.useCallback(function (type) {
    focusItemRef.current = type;
  }, []);

  var focusItem = function focusItem() {
    switch (focusItemRef.current) {
      case FOCUS_ELEMENTS.BUTTON:
        {
          var _buttonRef$current;

          buttonRef === null || buttonRef === void 0 ? void 0 : (_buttonRef$current = buttonRef.current) === null || _buttonRef$current === void 0 ? void 0 : _buttonRef$current.focus();
          break;
        }

      case FOCUS_ELEMENTS.LAST_ITEM:
        {
          var _lastItemRef$current;

          lastItemRef === null || lastItemRef === void 0 ? void 0 : (_lastItemRef$current = lastItemRef.current) === null || _lastItemRef$current === void 0 ? void 0 : _lastItemRef$current.focus();
          break;
        }

      case FOCUS_ELEMENTS.MENU:
        {
          var _menuRef$current;

          menuRef === null || menuRef === void 0 ? void 0 : (_menuRef$current = menuRef.current) === null || _menuRef$current === void 0 ? void 0 : _menuRef$current.focus();
          break;
        }
    }
  };

  var focusItemOnHide = function focusItemOnHide() {
    if (returnFocusRef) {
      returnFocusRef.current.focus();
      return;
    }

    focusItem();
  };

  var context = React.useMemo(function () {
    return {
      id: id,
      showingState: showingState,
      setShowingState: setShowingState,
      setCloseOnOutsideClick: setCloseOnOutsideClick,
      setFocusItem: setFocusItem,
      buttonRef: buttonRef,
      menuRef: menuRef,
      lastItemRef: lastItemRef
    };
  }, [id, setFocusItem, setShowingState, showingState]);

  var _React$Children$toArr = React.Children.toArray(children),
      _React$Children$toArr2 = _slicedToArray(_React$Children$toArr, 2),
      button = _React$Children$toArr2[0],
      menu = _React$Children$toArr2[1];

  if (!button || !menu) return null;
  return /*#__PURE__*/jsx(ButtonMenuContext.Provider, {
    value: context,
    children: /*#__PURE__*/jsx(Popper, _objectSpread(_objectSpread({}, rest), {}, {
      inline: inline,
      showing: showingState,
      onOutsideClick: function onOutsideClick() {
        if (closeOnOutsideClick) {
          focusItemRef.current = FOCUS_ELEMENTS.BUTTON;
          setShowingState(false);
        }
      },
      onHide: function onHide() {
        focusItemOnHide();

        _onHide();
      },
      onShow: function onShow() {
        focusItem();

        _onShow();
      },
      placement: position === "left" ? "bottom-start" : "bottom-end",
      renderTrigger: function renderTrigger(triggerProps) {
        return /*#__PURE__*/React.cloneElement(button, triggerProps);
      },
      renderContent: function renderContent(contentProps) {
        return /*#__PURE__*/React.cloneElement(menu, contentProps);
      }
    }))
  });
};

ButtonMenuContainer.propTypes = {
  "children": _PropTypes.node,
  "inline": _PropTypes.bool,
  "onHide": _PropTypes.func,
  "onShow": _PropTypes.func,
  "position": _PropTypes.oneOf(["left", "right"]),
  "returnFocusRef": _PropTypes.shape({
    current: _PropTypes.object
  }),
  "showing": _PropTypes.bool
};
ButtonMenuContainer.displayName = "ButtonMenuContainer";

export { ButtonMenuContainer, FOCUS_ELEMENTS, useButtonMenuContext };
